// 모바일 최적화를 위한 유틸리티 함수들

/**
 * 디바이스 타입 감지
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const isSafari = /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent);
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
  
  return {
    isMobile,
    isTablet,
    isIOS,
    isAndroid,
    isSafari,
    isInStandaloneMode,
    isDesktop: !isMobile && !isTablet,
    userAgent
  };
};

/**
 * 뷰포트 크기 감지
 */
export const getViewportInfo = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    ratio: window.devicePixelRatio,
    orientation: window.screen.orientation?.type || 'unknown',
    isPortrait: window.innerHeight > window.innerWidth,
    isLandscape: window.innerWidth > window.innerHeight
  };
};

/**
 * 안전영역 크기 계산 (노치, 홈 인디케이터 등)
 */
export const getSafeAreaInsets = () => {
  const style = getComputedStyle(document.documentElement);
  return {
    top: style.getPropertyValue('--sat') || style.getPropertyValue('env(safe-area-inset-top)') || '0px',
    right: style.getPropertyValue('--sar') || style.getPropertyValue('env(safe-area-inset-right)') || '0px',
    bottom: style.getPropertyValue('--sab') || style.getPropertyValue('env(safe-area-inset-bottom)') || '0px',
    left: style.getPropertyValue('--sal') || style.getPropertyValue('env(safe-area-inset-left)') || '0px'
  };
};

/**
 * 터치 디바이스 여부 확인
 */
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * 네트워크 상태 확인
 */
export const getNetworkInfo = () => {
  const navigator = window.navigator as any;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  return {
    isOnline: navigator.onLine,
    effectiveType: connection?.effectiveType || 'unknown',
    downlink: connection?.downlink || null,
    rtt: connection?.rtt || null,
    saveData: connection?.saveData || false
  };
};

/**
 * 배터리 상태 확인 (실험적 API)
 */
export const getBatteryInfo = async () => {
  try {
    const navigator = window.navigator as any;
    if ('getBattery' in navigator) {
      const battery = await navigator.getBattery();
      return {
        level: battery.level,
        charging: battery.charging,
        chargingTime: battery.chargingTime,
        dischargingTime: battery.dischargingTime
      };
    }
  } catch (error) {
    console.warn('배터리 API를 사용할 수 없습니다:', error);
  }
  return null;
};

/**
 * 햅틱 피드백 제공 (진동)
 */
export const hapticFeedback = (pattern: number | number[] = 100) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
};

/**
 * 화면 방향 잠금 (실험적 API)
 */
export const lockOrientation = async (orientation: 'portrait' | 'landscape' | 'portrait-primary' | 'landscape-primary') => {
  try {
    if ('orientation' in screen && 'lock' in screen.orientation) {
      await (screen.orientation as any).lock(orientation);
      return true;
    }
  } catch (error) {
    console.warn('화면 방향 잠금을 사용할 수 없습니다:', error);
  }
  return false;
};

/**
 * 화면 방향 해제 (실험적 API)
 */
export const unlockOrientation = async () => {
  try {
    if ('orientation' in screen && 'unlock' in screen.orientation) {
      (screen.orientation as any).unlock();
      return true;
    }
  } catch (error) {
    console.warn('화면 방향 해제를 사용할 수 없습니다:', error);
  }
  return false;
};

/**
 * 풀스크린 모드 진입
 */
export const enterFullscreen = async (element?: HTMLElement) => {
  const targetElement = element || document.documentElement;
  
  try {
    if (targetElement.requestFullscreen) {
      await targetElement.requestFullscreen();
    } else if ((targetElement as any).webkitRequestFullscreen) {
      await (targetElement as any).webkitRequestFullscreen();
    } else if ((targetElement as any).msRequestFullscreen) {
      await (targetElement as any).msRequestFullscreen();
    }
    return true;
  } catch (error) {
    console.warn('풀스크린 모드 진입 실패:', error);
    return false;
  }
};

/**
 * 풀스크린 모드 종료
 */
export const exitFullscreen = async () => {
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    }
    return true;
  } catch (error) {
    console.warn('풀스크린 모드 종료 실패:', error);
    return false;
  }
};

/**
 * 클립보드에 복사
 */
export const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    return false;
  } catch (error) {
    console.warn('클립보드 복사 실패:', error);
    return false;
  }
};

/**
 * 네이티브 공유 기능
 */
export const nativeShare = async (data: {
  title?: string;
  text?: string;
  url?: string;
}) => {
  try {
    if (navigator.share) {
      await navigator.share(data);
      return true;
    }
  } catch (error) {
    console.warn('네이티브 공유 실패:', error);
  }
  return false;
};

/**
 * 카메라 권한 확인
 */
export const checkCameraPermission = async () => {
  try {
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({ name: 'camera' as any });
      return permission.state;
    }
  } catch (error) {
    console.warn('카메라 권한 확인 실패:', error);
  }
  return 'unknown';
};

/**
 * 위치 권한 확인
 */
export const checkLocationPermission = async () => {
  try {
    if (navigator.permissions) {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      return permission.state;
    }
  } catch (error) {
    console.warn('위치 권한 확인 실패:', error);
  }
  return 'unknown';
};

/**
 * 현재 위치 가져오기
 */
export const getCurrentLocation = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('위치 서비스가 지원되지 않습니다.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
};

/**
 * 앱과 같은 스크롤 동작 최적화
 */
export const optimizeScrolling = () => {
  document.body.style.overscrollBehavior = 'none';
  document.documentElement.style.overscrollBehavior = 'none';
  document.documentElement.style.scrollBehavior = 'smooth';
  
  // iOS 터치 스크롤 최적화 (타입 단언 사용)
  (document.body.style as any).WebkitOverflowScrolling = 'touch';
};

/**
 * 키보드 높이 감지 (모바일)
 */
export const getKeyboardHeight = (): Promise<number> => {
  return new Promise((resolve) => {
    const initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleViewportChange = () => {
      const currentViewportHeight = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = initialViewportHeight - currentViewportHeight;
      resolve(Math.max(0, keyboardHeight));
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    } else {
      resolve(0);
    }
  });
};

/**
 * 앱 초기화 시 모바일 최적화 적용
 */
export const initializeMobileOptimizations = () => {
  const device = detectDevice();
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  
  if (device.isIOS && device.isSafari && !isStandalone) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        window.scrollTo(0, 1);
      }, 100);
    });
  }
  
  optimizeScrolling();
  
  document.addEventListener('touchstart', () => {}, { passive: true });
  document.addEventListener('touchmove', () => {}, { passive: true });
  document.addEventListener('touchend', () => {}, { passive: true });
  
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (event) => {
    const now = new Date().getTime();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, false);
  
  console.log('모바일 최적화 초기화 완료', device);
}; 

/**
 * iOS 사파리의 러버밴드(바운스) 스크롤을 최소화하는 no-bounce 처리
 * - 상/하단 끝에서 당길 때 스크롤 위치를 1px 밀어 오버스크롤 진입을 방지
 */
export const enableNoBounce = (targetElement?: HTMLElement) => {
  const device = detectDevice();
  if (!device.isIOS) return; // iOS에서만 적용

  const target = (targetElement as any) || (document.scrollingElement as any) || document.documentElement;
  if (!target) return;

  let startY = 0;

  const onTouchStart = (e: TouchEvent) => {
    if (!e.touches || e.touches.length === 0) return;
    startY = e.touches[0].clientY;

    // 상/하단 경계에서 1px 안쪽으로 강제 이동
    try {
      const scrollTop = target.scrollTop || 0;
      const clientHeight = target.clientHeight || window.innerHeight;
      const scrollHeight = target.scrollHeight || document.documentElement.scrollHeight;
      if (scrollTop <= 0) {
        target.scrollTop = 1;
      } else if (scrollTop + clientHeight >= scrollHeight) {
        target.scrollTop = scrollHeight - clientHeight - 1;
      }
    } catch {}
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!e.touches || e.touches.length === 0) return;
    try {
      const currentY = e.touches[0].clientY;
      const isPullingDown = currentY > startY;
      const isPullingUp = currentY < startY;
      const scrollTop = target.scrollTop || 0;
      const clientHeight = target.clientHeight || window.innerHeight;
      const scrollHeight = target.scrollHeight || document.documentElement.scrollHeight;

      // 상단에서 아래로 당기기 → 방지
      if (scrollTop <= 0 && isPullingDown) {
        target.scrollTop = 1;
        e.preventDefault();
        return;
      }
      // 하단에서 위로 당기기 → 방지
      if (scrollTop + clientHeight >= scrollHeight && isPullingUp) {
        target.scrollTop = scrollHeight - clientHeight - 1;
        e.preventDefault();
        return;
      }
    } catch {}
  };

  // iOS에서 preventDefault가 동작하려면 passive:false 여야 함
  document.addEventListener('touchstart', onTouchStart as any, { passive: false });
  document.addEventListener('touchmove', onTouchMove as any, { passive: false });
};